import { useTheme, Box, Typography, Paper, Select, MenuItem } from '@mui/material';
import { capitalizeFirstLetter } from 'state/helpers';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexCenter from 'components/FlexCenter';


const KPI_SKIP_KEYS = new Set(['_id', 'crewCode', 'crewName', 'kpi', 'description', 'codes']);

const formatVal = (v) => {
    if (v == null) return '—';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (typeof v === 'number') return v.toLocaleString('vi-VN');
    if (Array.isArray(v)) return v.join(', ') || '—';
    return String(v);
};

const CrewKPICard = ({ row, index, kpiOptions, onKpiChange }) => {
    const theme = useTheme();

    const entries = Object.entries(row).filter(([k]) => !KPI_SKIP_KEYS.has(k));
    const isWarning = row.kpi && row.kpi !== 'A';
    const hasCodes = row.codes && Object.keys(row.codes).length > 0;
    const codeText = hasCodes
        ? Object.entries(row.codes).sort((a, b) => b[1] - a[1]).map(([k, v]) => `${k}:${v}`).join(' · ')
        : null;

        
    return (
        <Paper elevation={1} sx={{ mb: 1, p: 1.5, borderLeft: `3px solid ${isWarning ? theme.palette.error.main : theme.palette.primary.main}`, borderRadius: 1 }}>
            <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    #{index + 1}
                </Typography>
                <Typography variant="subtitle2" noWrap flex={1} minWidth={0}>
                    {row.crewCode} {row.crewName}
                </Typography>
                <Select
                    size="small"
                    value={row.kpi || 'A'}
                    onChange={(e) => onKpiChange(row._id, e.target.value)}
                    sx={{
                        minWidth: 60,
                        height: 28,
                        fontSize: '0.8rem',
                        color: isWarning ? theme.palette.error.dark : 'inherit',
                        fontWeight: isWarning ? 'bold' : 'normal',
                    }}
                >
                    {kpiOptions.map(opt => (
                        <MenuItem key={opt} value={opt} sx={{ fontSize: '0.8rem' }}>{opt}</MenuItem>
                    ))}
                </Select>
            </Box>

            <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={0.75} mt={0.75}>
                {entries.map(([k, v]) => (
                    <Box key={k}>
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                            {capitalizeFirstLetter(k)}
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">{formatVal(v)}</Typography>
                    </Box>
                ))}
            </Box>

            {codeText && (
                <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
                    Codes: {codeText}
                </Typography>
            )}

            {row.description && (
                <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                    {row.description}
                </Typography>
            )}
        </Paper>
    );
};


const CrewKPIList = ({ rows, kpiOptions, onKpiChange }) => {
    if (!rows?.length) {
        return (
            <FlexCenter height={LAYOUT_HEIGHT}>
                <Typography color="error">No data available</Typography>
            </FlexCenter>
        );
    }

    return (
        <Box sx={{ px: 0.5, py: 0.5 }}>
            {rows.map((row, index) => (
                <CrewKPICard
                    key={row._id}
                    row={row}
                    index={index}
                    kpiOptions={kpiOptions}
                    onKpiChange={onKpiChange}
                />
            ))}
        </Box>
    );
};

export default CrewKPIList;
